/**
 * Event dates are calendar dates, not instants — format them as such.
 *
 * An event has two halves and they were different kinds of thing. `time` is a
 * string the organizer typed ("8:00 PM"), shown verbatim, with no timezone
 * anywhere near it. `date` is a Date: a point on a global timeline. Render an
 * instant in two zones and you get two answers, which is exactly what happened
 * — the event page said Wednesday 21 October while the Stripe payment page,
 * rendered by a server running in UTC, said Thursday 22 October. Same event,
 * two dates, on the two screens a buyer sees back to back.
 *
 * The fix is to treat the date the way the time is already treated: as a
 * calendar date that reads the same everywhere. Pinning every render to UTC
 * does that, because the browser, Railway and Stripe then all agree, and it
 * matches how an event is actually described — a party at 10pm in Houston is
 * on the poster as 10pm in Houston whether you read it from Toronto or Lagos.
 *
 * That is why this is not `timeZone: 'America/Chicago'` or the viewer's zone.
 * UTC here is not a claim about where the event is; it is a way of saying "do
 * not convert this". A real timezone per event is a separate, larger job — it
 * is what calendar exports, reminder emails and cross-zone online events need,
 * and it cannot be done well until every stored date is known to be consistent.
 */

/** Presets, so call sites cannot drift apart on formatting. */
export const EVENT_DATE = Object.freeze({
  short: { weekday: 'short', month: 'short', day: 'numeric' },
  medium: { month: 'short', day: 'numeric', year: 'numeric' },
  long: { weekday: 'long', month: 'long', day: 'numeric' },
  full: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
});

/**
 * Format an event's date without shifting it into the reader's timezone.
 *
 * @param {Date|string|number} value  the event's date
 * @param {object} options            Intl.DateTimeFormat options; timeZone is forced
 * @param {string} fallback           returned when the date is missing or unparseable
 */
export function formatEventDate(value, options = EVENT_DATE.short, fallback = '') {
  if (value === null || value === undefined || value === '') return fallback;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  // timeZone last, so no caller can accidentally override the one thing this
  // function exists to guarantee.
  return date.toLocaleDateString('en-US', { ...options, timeZone: 'UTC' });
}

/** The YYYY-MM-DD an <input type="date"> expects, without a timezone shift. */
export function toDateInputValue(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}
